// PDF viewer slot (S3): the real PDF.js page at fit-width times zoom with its selectable text layer, highlights for a requirement, clause or cited texts at their located positions, the page, zoom and highlight controls after the caller's toolbar start, a thumbnail rail when the viewer is wide, keyboard shortcuts, auto-scroll to the focus, and loading, not-located and problem states; page, zoom and highlights follow the props and report changes.
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeOffIcon,
  InfoIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";

import { ProblemState } from "@/components/common/ProblemState";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/cn";
import { formatNumber } from "@/lib/format";
import { useMediaQuery } from "@/lib/use-media-query";

import {
  focusKey,
  scrollAnchor,
  usePageMarks,
  type PdfFocus,
  type ScrollTarget,
  type ViewerMarks,
} from "./highlights";
import { HighlightLayer } from "./HighlightLayer";
import { PagePlaceholder, PdfPage, usePdfPage } from "./PdfPage";
import { Thumbnails } from "./Thumbnails";
import { usePdfDocument } from "./usePdfDocument";
import { useViewerHotkeys } from "./useViewerHotkeys";
import "./pdf-viewer.css";

export type { PdfFocus } from "./highlights";

export interface PdfViewerProps {
  docUid: string;
  page: number;
  onPageChange?: (page: number) => void;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  highlightsVisible?: boolean;
  onHighlightsVisibleChange?: (v: boolean) => void;
  focus?: PdfFocus;
  activeValueKey?: string | null;
  flashValues?: boolean;
  scrollTo?: { target: "clause" | "requirement" | "figure" | "top"; nonce: number } | null;
  showThumbnails?: boolean;
  toolbarStart?: ReactNode;
  className?: string;
}

export const ZOOM_STEPS = [0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5] as const;
const MIN_ZOOM = ZOOM_STEPS[0];
const MAX_ZOOM = ZOOM_STEPS[ZOOM_STEPS.length - 1] ?? 2.5;
const A4_WIDTH_PT = 595.32;
const A4_HEIGHT_PT = 841.92;
const CANVAS_PADDING_PX = 20;
const DEFAULT_WIDTH_PX = 640;
const MAX_FIT_WIDTH_PX = 980;
const SCROLL_OFFSET_PX = 140;
const THUMBNAIL_MIN_WIDTH_PX = 720;

export function nextZoom(zoom: number, direction: 1 | -1): number {
  const steps =
    direction === 1
      ? ZOOM_STEPS.filter((step) => step > zoom + 0.001)
      : ZOOM_STEPS.filter((step) => step < zoom - 0.001);
  const next = direction === 1 ? steps[0] : steps.at(-1);
  return next ?? (direction === 1 ? MAX_ZOOM : MIN_ZOOM);
}

export function fitScale(containerWidth: number, pageWidth: number): number {
  const width = Math.min(
    Math.max(containerWidth, 0) || DEFAULT_WIDTH_PX,
    MAX_FIT_WIDTH_PX + 2 * CANVAS_PADDING_PX,
  );
  return Math.max(0.1, (width - 2 * CANVAS_PADDING_PX) / pageWidth);
}

function clampZoom(zoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

function useFollowingState<T>(value: T, onChange: ((value: T) => void) | undefined): [T, (value: T) => void] {
  const [state, setState] = useState(value);
  const [seen, setSeen] = useState(value);
  if (!Object.is(value, seen)) {
    setSeen(value);
    setState(value);
  }
  const update = useCallback(
    (next: T) => {
      setState(next);
      onChange?.(next);
    },
    [onChange],
  );
  return [state, update];
}

function useElementWidth(element: HTMLElement | null): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (element === null || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      const next = element.clientWidth;
      setWidth((current) => (current === next ? current : next));
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [element]);
  return width;
}

function IconTip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function PageControls({
  page,
  numPages,
  onChange,
  className,
}: {
  page: number;
  numPages: number | null;
  onChange: (page: number) => void;
  className?: string | undefined;
}) {
  const inputId = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) {
      return;
    }
    const wanted = Number.parseInt(draft, 10);
    setDraft(null);
    if (Number.isFinite(wanted) && numPages !== null) {
      onChange(Math.min(numPages, Math.max(1, wanted)));
    }
  };
  return (
    <div
      className={cn(
        "flex items-center gap-1 text-xs whitespace-nowrap text-muted-foreground tabular-nums",
        className,
      )}
    >
      <IconTip label="Previous page">
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => {
            onChange(page - 1);
          }}
        >
          <ChevronLeftIcon aria-hidden />
        </Button>
      </IconTip>
      <label htmlFor={inputId} className="sr-only">
        Page number
      </label>
      <input
        id={inputId}
        inputMode="numeric"
        autoComplete="off"
        className="h-7 w-11 rounded-sm border border-transparent bg-transparent text-center text-xs text-foreground tabular-nums outline-none hover:border-input focus-visible:border-input-focus focus-visible:shadow-[0_0_0_3px_var(--input-ring)]"
        value={draft ?? String(page)}
        onChange={(event) => {
          setDraft(event.target.value.replace(/\D/gu, ""));
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            commit();
          } else if (event.key === "Escape") {
            setDraft(null);
          }
        }}
      />
      <span aria-hidden>/ {numPages === null ? "…" : formatNumber(numPages)}</span>
      <span className="sr-only">{numPages === null ? "" : `of ${formatNumber(numPages)} pages`}</span>
      <IconTip label="Next page">
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Next page"
          disabled={numPages === null || page >= numPages}
          onClick={() => {
            onChange(page + 1);
          }}
        >
          <ChevronRightIcon aria-hidden />
        </Button>
      </IconTip>
    </div>
  );
}

function ZoomControls({ zoom, onChange }: { zoom: number; onChange: (zoom: number) => void }) {
  const percent = Math.round(zoom * 100);
  return (
    <div className="flex items-center gap-1 text-xs whitespace-nowrap text-muted-foreground tabular-nums">
      <IconTip label="Zoom out">
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Zoom out"
          disabled={zoom <= MIN_ZOOM}
          onClick={() => {
            onChange(nextZoom(zoom, -1));
          }}
        >
          <ZoomOutIcon aria-hidden />
        </Button>
      </IconTip>
      <IconTip label="Fit to width">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-12 px-0 text-xs font-normal text-muted-foreground"
          aria-label={`Zoom ${String(percent)} %, fit to width`}
          onClick={() => {
            onChange(1);
          }}
        >
          {percent} %
        </Button>
      </IconTip>
      <IconTip label="Zoom in">
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Zoom in"
          disabled={zoom >= MAX_ZOOM}
          onClick={() => {
            onChange(nextZoom(zoom, 1));
          }}
        >
          <ZoomInIcon aria-hidden />
        </Button>
      </IconTip>
    </div>
  );
}

function HighlightsToggle({ visible, onChange }: { visible: boolean; onChange: (visible: boolean) => void }) {
  return (
    <IconTip label={visible ? "Hide highlights" : "Show highlights"}>
      <Button
        variant="outline"
        size="icon-sm"
        aria-label="Highlights"
        aria-pressed={visible}
        onClick={() => {
          onChange(!visible);
        }}
      >
        {visible ? <EyeIcon aria-hidden /> : <EyeOffIcon aria-hidden />}
      </Button>
    </IconTip>
  );
}

export function locatedNote(focus: PdfFocus, marks: ViewerMarks | null, page: number): string | null {
  if (focus === null || marks?.textFound !== false) {
    return null;
  }
  if (focus.kind === "texts") {
    return `The cited text was not located on page ${String(page)}.`;
  }
  if (marks.clause.length > 0) {
    return `Exact text not located on page ${String(page)}; showing the clause heading.`;
  }
  return `The requirement text is not on page ${String(page)}.`;
}

function Note({ children }: { children: ReactNode }) {
  return (
    <p
      role="status"
      className="mx-auto mt-0 mb-3 flex max-w-[640px] items-start gap-2 rounded-md bg-card px-3 py-2 text-xs text-ink-2 shadow-1"
    >
      <InfoIcon aria-hidden className="mt-px size-3.5 shrink-0 text-brand-blue" />
      {children}
    </p>
  );
}

export function PdfViewer(props: PdfViewerProps) {
  const {
    docUid,
    focus = null,
    activeValueKey = null,
    flashValues = false,
    scrollTo = null,
    showThumbnails = true,
    toolbarStart,
    className,
  } = props;
  const [page, setPage] = useFollowingState(props.page, props.onPageChange);
  const [zoom, setZoom] = useFollowingState(clampZoom(props.zoom ?? 1), props.onZoomChange);
  const [visible, setVisible] = useFollowingState(
    props.highlightsVisible ?? true,
    props.onHighlightsVisibleChange,
  );
  const document = usePdfDocument(docUid);
  const numPages = document.doc?.numPages ?? null;
  const current = numPages === null ? Math.max(1, page) : Math.min(numPages, Math.max(1, page));
  const pdfPage = usePdfPage(document.doc, current);
  const marks = usePageMarks(docUid, current, focus);
  const [section, setSection] = useState<HTMLElement | null>(null);
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const width = useElementWidth(scroller);
  const sectionWidth = useElementWidth(section);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [renderedPage, setRenderedPage] = useState<number | null>(null);
  const pageElement = useRef<HTMLDivElement>(null);
  const performed = useRef<{ auto: string | null; explicit: number | null }>({ auto: null, explicit: null });
  const started = useRef<number | null>(null);
  const [firstRenderMs, setFirstRenderMs] = useState<number | null>(null);

  useEffect(() => {
    started.current = performance.now();
  }, [docUid]);

  const base = useMemo(() => pdfPage.page?.getViewport({ scale: 1 }) ?? null, [pdfPage.page]);
  const scale = fitScale(width, base?.width ?? A4_WIDTH_PT) * zoom;
  const viewport = useMemo(() => pdfPage.page?.getViewport({ scale }) ?? null, [pdfPage.page, scale]);
  const key = focusKey(focus);

  const goTo = useCallback(
    (next: number) => {
      setPage(numPages === null ? Math.max(1, next) : Math.min(numPages, Math.max(1, next)));
    },
    [numPages, setPage],
  );

  useViewerHotkeys(section, {
    zoomIn: () => {
      setZoom(nextZoom(zoom, 1));
    },
    zoomOut: () => {
      setZoom(nextZoom(zoom, -1));
    },
    fit: () => {
      setZoom(1);
    },
    next: () => {
      goTo(current + 1);
    },
    previous: () => {
      goTo(current - 1);
    },
  });

  const ready = viewport !== null && renderedPage === current && (focus === null || !marks.isLoading);
  useEffect(() => {
    if (!ready || scroller === null) {
      return;
    }
    const explicit = scrollTo !== null && performed.current.explicit !== scrollTo.nonce ? scrollTo : null;
    const autoId = `${key ?? "none"}@${String(current)}`;
    let target: ScrollTarget | null = null;
    if (explicit !== null) {
      target = explicit.target;
      performed.current = { auto: autoId, explicit: explicit.nonce };
    } else if (performed.current.auto !== autoId) {
      performed.current = { ...performed.current, auto: autoId };
      target = focus === null ? "top" : focus.kind === "clause" ? "clause" : "requirement";
    }
    if (target === null) {
      return;
    }
    const anchor = scrollAnchor(marks.marks, target);
    const pageTop = pageElement.current?.offsetTop ?? 0;
    const top = anchor === null ? 0 : Math.max(0, pageTop + anchor * viewport.scale - SCROLL_OFFSET_PX);
    scroller.scrollTo({ top, behavior: reducedMotion ? "auto" : "smooth" });
  }, [ready, scroller, viewport, scrollTo, key, current, focus, marks.marks, reducedMotion]);

  const onRendered = useCallback(
    (rendered: number) => {
      setRenderedPage(rendered);
      const start = started.current;
      if (firstRenderMs === null && start !== null) {
        const elapsed = Math.round(performance.now() - start);
        setFirstRenderMs(elapsed);
        performance.measure("gridcode:pdf-first-render", { start, duration: elapsed });
      }
    },
    [firstRenderMs],
  );

  const note = locatedNote(focus, marks.marks, current);
  const thumbnailDoc = showThumbnails && sectionWidth >= THUMBNAIL_MIN_WIDTH_PX ? document.doc : null;
  const placeholderWidth = (base?.width ?? A4_WIDTH_PT) * scale;
  const placeholderHeight = (base?.height ?? A4_HEIGHT_PT) * scale;

  let content: ReactNode;
  if (document.status === "error") {
    content = (
      <ProblemState
        problem={document.error}
        retry={document.retry}
        className="mx-auto max-w-[560px] rounded-lg bg-card"
      />
    );
  } else if (pdfPage.error !== null) {
    content = (
      <ProblemState
        problem={pdfPage.error}
        retry={document.retry}
        className="mx-auto max-w-[560px] rounded-lg bg-card"
      />
    );
  } else if (pdfPage.page === null || viewport === null) {
    content = <PagePlaceholder width={placeholderWidth} height={placeholderHeight} />;
  } else {
    content = (
      <div ref={pageElement}>
        <PdfPage
          page={pdfPage.page}
          viewport={viewport}
          label={`Page ${String(current)}`}
          onRendered={onRendered}
        >
          <HighlightLayer
            marks={marks.marks}
            scale={viewport.width / (marks.marks?.pageWidth ?? viewport.width / viewport.scale)}
            visible={visible}
            activeValueKey={activeValueKey}
            flashValues={flashValues}
            scanKey={key === null || !visible ? null : `${key}@${String(current)}`}
          />
        </PdfPage>
      </div>
    );
  }

  return (
    <section
      ref={setSection}
      aria-label="Source"
      aria-busy={document.status === "loading" || (focus !== null && marks.isLoading)}
      data-render-ms={firstRenderMs ?? undefined}
      className={cn("flex h-full min-h-0 min-w-0 flex-col bg-viewer", className)}
    >
      <div className="flex flex-wrap items-center gap-2.5 border-b bg-card px-3 py-2">
        {toolbarStart}
        <PageControls
          page={current}
          numPages={numPages}
          onChange={goTo}
          className={toolbarStart === undefined ? "mr-auto" : undefined}
        />
        <ZoomControls zoom={zoom} onChange={setZoom} />
        {focus === null ? null : <HighlightsToggle visible={visible} onChange={setVisible} />}
      </div>
      <div className="flex min-h-0 flex-1">
        {thumbnailDoc !== null && numPages !== null ? (
          <Thumbnails
            doc={thumbnailDoc}
            page={current}
            numPages={numPages}
            marks={marks.marks}
            onSelect={goTo}
          />
        ) : null}
        <ScrollArea
          ref={setScroller}
          tabIndex={0}
          role="region"
          orientation="both"
          aria-label={
            numPages === null
              ? `Page ${String(current)}`
              : `Page ${String(current)} of ${formatNumber(numPages)}`
          }
          className="relative flex-1 px-5 pt-6 pb-10 outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
        >
          {marks.error !== null && marks.error !== undefined ? (
            <ProblemState
              problem={marks.error}
              retry={marks.refetch}
              compact
              className="mx-auto mb-3 max-w-[640px]"
            />
          ) : null}
          {note === null || !visible ? null : <Note>{note}</Note>}
          {content}
        </ScrollArea>
      </div>
    </section>
  );
}
